"use client";

import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Card } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { formatCurrency } from "@/lib/utils";
import type { SaleHistory } from "@/schemas/analyzed-property";

interface PriceHistoryEntry {
  date: string;
  price: number;
  event: string;
  pricePerSquareFoot?: number;
  source?: string;
}

interface PricingHistoryProps {
  priceHistory?: PriceHistoryEntry[];
  saleHistory?: SaleHistory[];
  propertyJsonData?: Record<string, unknown> | null;
}

interface ChartDataPoint {
  date: string;
  price: number;
  event?: string;
  saleInfo?: {
    buyer: string;
    seller: string;
    method?: string;
    type: string;
  } | null;
}

/**
 * Checks if two dates are within a specified number of days of each other
 */
function areDatesClose(
  date1: string,
  date2: string,
  daysThreshold = 7,
): boolean {
  try {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2.getTime() - d1.getTime());
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    return diffDays <= daysThreshold;
  } catch (error) {
    return false;
  }
}

/**
 * Extracts price history from Zillow property JSON data
 */
function extractZillowPriceHistory(
  propertyJsonData: Record<string, unknown> | null | undefined,
): PriceHistoryEntry[] {
  if (!propertyJsonData) return [];

  // Handle different possible locations of price history in the Zillow data
  let priceHistory: unknown[] = [];

  if (Array.isArray(propertyJsonData.priceHistory)) {
    priceHistory = propertyJsonData.priceHistory as unknown[];
  } else if (Array.isArray(propertyJsonData.price_history)) {
    priceHistory = propertyJsonData.price_history as unknown[];
  } else if (
    typeof propertyJsonData.propertyHistory === "object" &&
    propertyJsonData.propertyHistory !== null &&
    Array.isArray(
      (propertyJsonData.propertyHistory as { events?: unknown[] }).events,
    )
  ) {
    const events =
      (propertyJsonData.propertyHistory as { events?: unknown[] }).events || [];
    priceHistory = events.filter(
      (e) => typeof e === "object" && e !== null && "price" in e,
    );
  }

  if (priceHistory.length === 0) {
    return [];
  }

  return priceHistory
    .map((entry) => {
      if (typeof entry !== "object" || entry === null) {
        return {
          date: "",
          price: 0,
          event: "Unknown",
          source: "Zillow",
        };
      }

      const entryObj = entry as Record<string, unknown>;

      // Handle different data formats from Zillow
      const date =
        (typeof entryObj.date === "string" ? entryObj.date : "") ||
        (typeof entryObj.time === "string" ? entryObj.time : "") ||
        (typeof entryObj.eventDate === "string" ? entryObj.eventDate : "") ||
        "";

      let price = 0;
      if (typeof entryObj.price === "number") {
        price = entryObj.price;
      } else if (typeof entryObj.price === "string") {
        price = Number.parseFloat(
          entryObj.price.replace(/[^\d.-]/g, "") || "0",
        );
      }

      const event =
        (typeof entryObj.event === "string" ? entryObj.event : "") ||
        (typeof entryObj.type === "string" ? entryObj.type : "") ||
        (typeof entryObj.eventType === "string" ? entryObj.eventType : "") ||
        "Price Change";

      return {
        date,
        price,
        event,
        source: "Zillow",
      };
    })
    .filter((entry) => entry.date && entry.price > 0);
}

/**
 * Merges price history and sale history data, handling duplicates
 */
function mergeHistoryData(
  priceHistory: PriceHistoryEntry[] = [],
  saleHistory: SaleHistory[] = [],
  zillowPriceHistory: PriceHistoryEntry[] = [],
): ChartDataPoint[] {
  // Convert price history entries to chart data points
  const priceDataPoints: ChartDataPoint[] = priceHistory.map((entry) => ({
    date: entry.date,
    price: entry.price,
    event: entry.event,
    saleInfo: null,
  }));

  // Convert sale history entries to chart data points
  const saleDataPoints: ChartDataPoint[] = saleHistory.map((entry) => ({
    date: entry.date,
    price: entry.amount,
    event: "Sold",
    saleInfo: {
      buyer: entry.buyer,
      seller: entry.seller,
      method: entry.purchase_method,
      type: entry.transaction_type,
    },
  }));

  // Convert Zillow price history to chart data points
  const zillowDataPoints: ChartDataPoint[] = zillowPriceHistory.map(
    (entry) => ({
      date: entry.date,
      price: entry.price,
      event: entry.event,
      saleInfo: null,
    }),
  );

  // Combine all data points
  const allDataPoints = [
    ...priceDataPoints,
    ...saleDataPoints,
    ...zillowDataPoints,
  ];

  // Sort by date (oldest first)
  allDataPoints.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  // Remove duplicates (entries with same date and price or very close dates with same price)
  const deduplicatedPoints: ChartDataPoint[] = [];

  for (const point of allDataPoints) {
    const isDuplicate = deduplicatedPoints.some(
      (existingPoint) =>
        existingPoint.price === point.price &&
        (existingPoint.date === point.date ||
          areDatesClose(existingPoint.date, point.date)),
    );

    if (!isDuplicate) {
      // If sale info exists in both, prefer the one with more complete info
      const existingWithCloseDate = deduplicatedPoints.find((existingPoint) =>
        areDatesClose(existingPoint.date, point.date),
      );

      if (
        existingWithCloseDate &&
        point.saleInfo &&
        existingWithCloseDate.saleInfo
      ) {
        // Both have sale info - keep the more detailed one
        if (
          (point.saleInfo.buyer && !existingWithCloseDate.saleInfo.buyer) ||
          (point.saleInfo.method && !existingWithCloseDate.saleInfo.method)
        ) {
          // Replace the existing point with this one
          const index = deduplicatedPoints.indexOf(existingWithCloseDate);
          deduplicatedPoints[index] = point;
        }
      } else {
        deduplicatedPoints.push(point);
      }
    }
  }

  return deduplicatedPoints;
}

export function PricingHistory({
  priceHistory = [],
  saleHistory = [],
  propertyJsonData,
}: PricingHistoryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [chartDimensions, setChartDimensions] = useState({
    width: 0,
    height: 240,
  });

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setChartDimensions({
          width: containerRef.current.clientWidth,
          height: 240,
        });
      }
    };

    // Initial size
    updateDimensions();

    // Update on resize
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => {
      if (containerRef.current) {
        observer.unobserve(containerRef.current);
      }
    };
  }, []);

  // Extract Zillow price history if available
  const zillowPriceHistory = extractZillowPriceHistory(propertyJsonData);

  // Merge and deduplicate data from all sources
  const chartData = mergeHistoryData(
    priceHistory,
    saleHistory,
    zillowPriceHistory,
  );

  // Only show if we have at least 2 data points
  if (chartData.length < 2) return null;

  const chartConfig = {
    price: {
      label: "Price",
      color: "#CF9D7C", // plaster color
    },
  };

  // Calculate min and max values for better axis visualization
  const prices = chartData.map((point) => point.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  // Add 10% padding to min/max for better visualization
  const yAxisDomain = [
    minPrice - (maxPrice - minPrice) * 0.05,
    maxPrice + (maxPrice - minPrice) * 0.05,
  ];

  return (
    <Card className="overflow-hidden">
      <div className="pl-2 pr-6 py-6 space-y-4">
        <div className="flex flex-col gap-1.5 pl-4">
          <h3 className="text-lg font-semibold">Pricing History</h3>
          <p className="text-sm text-muted-foreground">
            Historical pricing trends for this property
          </p>
        </div>

        <div ref={containerRef} className="h-[240px] sm:h-[240px] w-full">
          <ChartContainer config={chartConfig} className="aspect-auto h-full">
            <>
              {chartDimensions.width > 0 ? (
                <AreaChart
                  width={chartDimensions.width}
                  height={chartDimensions.height}
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 5, bottom: 25 }}
                >
                  <defs>
                    <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="#CF9D7C"
                        stopOpacity={0.25}
                      />
                      <stop offset="95%" stopColor="#CF9D7C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    vertical={false}
                    opacity={0.3}
                    stroke="#e5e7eb"
                  />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date: string) => {
                      try {
                        return format(new Date(date), "MMM yyyy");
                      } catch {
                        return "";
                      }
                    }}
                    tick={{ fontSize: 12, fill: "#4b5563" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e5e7eb" }}
                    minTickGap={30}
                    dy={8}
                    padding={{ left: 5, right: 5 }}
                  />
                  <YAxis
                    tickFormatter={(value: number) => formatCurrency(value)}
                    tick={{ fontSize: 12, fill: "#4b5563" }}
                    tickLine={false}
                    axisLine={{ stroke: "#e5e7eb" }}
                    width={75}
                    domain={yAxisDomain}
                    padding={{ top: 10, bottom: 10 }}
                  />
                  <RechartsTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;

                      const data = payload[0].payload;
                      return (
                        <div className="rounded-lg border bg-white p-3 shadow-lg space-y-2">
                          <div className="space-y-1">
                            <span className="text-xs uppercase text-gray-500 font-medium">
                              {format(new Date(data.date), "MMM d, yyyy")}
                            </span>
                            <div className="font-semibold text-lg">
                              {formatCurrency(data.price)}
                            </div>
                            {data.event && (
                              <div className="text-sm text-gray-600 font-medium">
                                {data.event}
                              </div>
                            )}
                          </div>

                          {data.saleInfo && (
                            <div className="space-y-1 border-t pt-2 text-sm">
                              <div className="flex items-center gap-1.5 text-gray-600">
                                <span className="truncate max-w-[120px]">
                                  {data.saleInfo.seller}
                                </span>
                                <ArrowRight className="h-3.5 w-3.5" />
                                <span className="truncate max-w-[120px]">
                                  {data.saleInfo.buyer}
                                </span>
                              </div>
                              {data.saleInfo.method && (
                                <div className="text-sm text-gray-600">
                                  {data.saleInfo.method}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    }}
                    cursor={{ stroke: "#9ca3af", strokeWidth: 1 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="price"
                    stroke="#CF9D7C"
                    strokeWidth={2.5}
                    fill="url(#colorPrice)"
                    dot={{
                      r: 4,
                      fill: "white",
                      stroke: "#CF9D7C",
                      strokeWidth: 2,
                    }}
                    activeDot={{
                      r: 6,
                      fill: "white",
                      stroke: "#CF9D7C",
                      strokeWidth: 2.5,
                      strokeDasharray: "",
                    }}
                  />
                </AreaChart>
              ) : null}
            </>
          </ChartContainer>
        </div>
      </div>
    </Card>
  );
}
